import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View, } from "react-native";
import Animated, { useAnimatedStyle, interpolate, Extrapolation, } from 'react-native-reanimated';
import { ReadiumView, } from 'react-native-readium';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDebounce } from 'use-debounce';
import { useQueryClient } from '@tanstack/react-query';
import { getContentProgress, syncProgress, useProgressItemMutation } from '@annunciate/query';
import { analytics, isNetworkConnected, onAVStarted, PlaybackEvent, PlaybackEventEmitter, PositionType, useAuthStateStore, usePlaybackStateStore, } from "@annunciate/core";
import { FAIcon } from '../elements';
import { useReaderColors, useReaderPreferences } from '../../hooks/player';
import { TableOfContents, ReaderSettings } from './buttons';
import { contentToEbookTrack } from '../../utils/player';
import { BookmarkButton } from '../bookmark';
import { useEbookPlaybackState } from './useEbookPlaybackState';
import { useWindowHeight } from '../../hooks';
import { makeEbookLocatorCompatible } from '../../utils';
const { isAuthValid, uuid } = useAuthStateStore.getState();
const initializeReaderAnalytics = async (position) => {
    const { playerIndex, content } = usePlaybackStateStore.getState();
    if (!content?.id)
        return;
    await analytics.emitEbookEvent("started", content, position);
    onAVStarted(content.id, position.locations?.totalProgression || 0, false);
    if (playerIndex !== 1) {
        await analytics.emitEbookEvent("pausing", content, position);
    }
};
export const Reader = ({ playing, onTogglePlayer, position, }) => {
    const ref = useRef(undefined);
    const color = useReaderColors();
    const preferences = useReaderPreferences();
    const [toc, setToc] = useState([]);
    const [file, setFile] = useState();
    const [initialLocation, setInitialLocation] = useState();
    const [location, setLocation] = useState();
    const [debounceLocation] = useDebounce(location, 1000, {
        leading: true,
        trailing: true,
    });
    const { mutate } = useProgressItemMutation();
    const client = useQueryClient();
    const insets = useSafeAreaInsets();
    const hasAuthInitialized = useAuthStateStore((state) => state.hasAuthInitialized);
    useEbookPlaybackState();
    const windowHeight = useWindowHeight();
    const onScrubListener = useCallback(async ({ content, to, }) => {
        if (content.id !== playing.id)
            return;
        if (to.type !== PositionType.EBOOK)
            return;
        const toLocation = to.location;
        makeEbookLocatorCompatible(toLocation);
        const toTotalProgression = toLocation.locations?.totalProgression;
        await analytics.emitEbookEvent("pausing", content);
        await analytics.emitPlaybackEvent("seekStarted", {
            seek_position: toTotalProgression
        });
        setLocation(toLocation);
        return;
    }, [playing.id, setLocation]);
    useEffect(() => {
        PlaybackEventEmitter.on(PlaybackEvent.SCRUB, onScrubListener);
        return () => {
            PlaybackEventEmitter.off(PlaybackEvent.SCRUB, onScrubListener);
        };
    }, []);
    useEffect(() => {
        if (!hasAuthInitialized || !client) {
            setInitialLocation(undefined);
            return;
        }
        const initializeReaderPosition = async () => {
            const initialPosition = usePlaybackStateStore.getState().initialPosition;
            if (initialPosition && initialPosition.type === PositionType.EBOOK) {
                const { location: resumeLocation } = initialPosition;
                makeEbookLocatorCompatible(resumeLocation);
                setLocation(resumeLocation);
                setInitialLocation(resumeLocation);
                usePlaybackStateStore.setState({ initialPosition: undefined });
                initializeReaderAnalytics(resumeLocation);
                return;
            }
            if (!isAuthValid() && uuid && await isNetworkConnected()) {
                setInitialLocation(undefined);
                return;
            }
            try {
                await syncProgress(client);
            }
            catch (error) {
                console.error("syncProgress Error", error);
            }
            const progressItem = getContentProgress(client, playing.id);
            if (progressItem?.position?.type === PositionType.EBOOK) {
                const { location: resumeLocation } = progressItem.position;
                makeEbookLocatorCompatible(resumeLocation);
                setLocation(resumeLocation);
                setInitialLocation(resumeLocation);
                initializeReaderAnalytics(resumeLocation);
            }
            else {
                setInitialLocation(null);
                initializeReaderAnalytics({
                    href: "",
                    type: "",
                    locations: {
                        totalProgression: 0,
                        progression: 0,
                    }
                });
            }
        };
        initializeReaderPosition();
    }, [playing.id, hasAuthInitialized, !!client]);
    useEffect(() => {
        if (playing && playing.asset?.src) {
            setFile(contentToEbookTrack(playing));
        }
        else {
            setFile(undefined);
        }
    }, [playing.id, playing.asset?.src]);
    const rotation = useAnimatedStyle(() => {
        const opacity = interpolate(position.value, [1, 0.5], [1, 0], Extrapolation.CLAMP);
        const deg = interpolate(position.value, [1, 0.75], [0, 180], Extrapolation.CLAMP);
        return {
            opacity,
            transform: [{
                    rotate: `${deg}deg`,
                }],
        };
    });
    if (initialLocation === undefined || !file || !hasAuthInitialized) {
        return (React.createElement(View, null,
            React.createElement(ActivityIndicator, { color: color.backgroundInverted })));
    }
    return (React.createElement(View, { style: {
            backgroundColor: color.background,
            height: windowHeight - insets.top,
            paddingBottom: insets.bottom,
            paddingLeft: insets.left,
            paddingRight: insets.right,
        } },
        React.createElement(View, { style: styles.controls },
            React.createElement(View, { style: styles.leftButtonContainer },
                React.createElement(Pressable, { onPress: onTogglePlayer, style: styles.button },
                    React.createElement(Animated.View, { style: rotation },
                        React.createElement(FAIcon, { name: "chevron-up", color: color.backgroundInverted })))),
            React.createElement(View, { style: styles.rightButtonContainer },
                React.createElement(TableOfContents, { items: toc, onPress: (link) => {
                        const position = {
                            type: PositionType.EBOOK,
                            location: {
                                href: link.href,
                                type: "application/xhtml+xml",
                                title: link.title || "",
                            },
                        };
                        PlaybackEventEmitter.emit(PlaybackEvent.SCRUB, {
                            content: playing,
                            to: position,
                        });
                    } }),
                React.createElement(BookmarkButton, { item: playing, size: 30, buttonStyle: {
                        paddingVertical: 0,
                        paddingHorizontal: 0,
                    }, iconColor: color.backgroundInverted }),
                React.createElement(View, null,
                    React.createElement(ReaderSettings, null)))),
        React.createElement(View, { style: { flex: 1 } },
            React.createElement(ReadiumView, { ref: ref, style: { flex: 1 }, file: {
                    ...file,
                    initialLocation,
                }, location: debounceLocation, preferences: preferences, onLocationChange: async (newLocation) => {
                    setLocation(newLocation);
                    const position = {
                        type: PositionType.EBOOK,
                        location: newLocation,
                    };
                    const currentPlayerIndex = usePlaybackStateStore.getState().playerIndex;
                    if (currentPlayerIndex !== 1) {
                        return;
                    }
                    const IGNORE_SKIP_THRESHOLD = 5;
                    if (debounceLocation?.locations?.position &&
                        newLocation.locations?.position &&
                        debounceLocation.locations.position >
                            newLocation.locations.position - IGNORE_SKIP_THRESHOLD) {
                        analytics.emitEbookEvent("playing", playing, newLocation);
                    }
                    else {
                        await analytics.emitEbookEvent("pausing", playing);
                        await analytics.emitPlaybackEvent("seekStarted", {
                            seek_position: newLocation.locations?.totalProgression,
                        });
                        await analytics.emitEbookEvent("playing", playing, newLocation);
                    }
                    mutate({
                        contentId: playing.id,
                        position,
                        sync: true,
                    });
                }, onTableOfContents: (toc) => setToc(toc) }))));
};
const styles = StyleSheet.create({
    controls: {
        flexDirection: 'row',
        padding: 10,
    },
    leftButtonContainer: {
        flexGrow: 1,
        alignItems: 'flex-start',
        flexDirection: 'row',
    },
    button: {
        flexGrow: 1,
        alignItems: 'flex-start',
    },
    rightButtonContainer: {
        marginLeft: 'auto',
        flexDirection: 'row',
        gap: 16,
    },
});
//# sourceMappingURL=Reader.js.map